import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Sizes } from '@/components/ui/types';

import {
  AvatarGroupProps,
  AvatarProps,
  AvatarShape,
  AvatarStatus,
} from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'w-8',
  sm: 'w-12',
  md: 'w-16',
  lg: 'w-20',
  xl: 'w-24',
};

const shapeClasses: Record<AvatarShape, string> = {
  circle: 'rounded-full',
  rounded: 'rounded-xl',
  squircle: 'mask mask-squircle',
  hexagon: 'mask mask-hexagon-2',
};

const statusClasses: Record<AvatarStatus, string> = {
  online: 'avatar-online',
  offline: 'avatar-offline',
};

const AvatarBase = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      size = 'md',
      shape = 'circle',
      status,
      placeholder = false,
      children,
      ...props
    },
    ref,
  ) => {
    const wrapperClasses = twMerge(
      'avatar',
      placeholder && 'avatar-placeholder',
      status && statusClasses[status],
      className,
    );

    const innerClasses = twMerge(
      sizeClasses[size],
      shapeClasses[shape],
      placeholder && 'bg-base-200 text-base-content border border-base-300',
    );

    return (
      <div ref={ref} className={wrapperClasses} {...props}>
        <div className={innerClasses}>{children}</div>
      </div>
    );
  },
);

const Group = forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('avatar-group -space-x-6', className)}
      {...props}
    >
      {children}
    </div>
  ),
);

AvatarBase.displayName = 'Avatar';
Group.displayName = 'Avatar.Group';

export const Avatar = Object.assign(AvatarBase, {
  Group,
});
